import React, { useState } from "react";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import apiUrl from "../components/Url";
import ThankYou from "../components/ThankYou";
import { useLanguage } from '../i18n/translations';

const stores = [
  {
    id: 1,
    name: "Katkus Shop",
    address: "Buka 4",
    city: "Sarajevo",
    zip: "71000",
    country: "Bosna i Hercegovina",

    lat: 43.8476,
    lng: 18.3564,
    mapUrl:
      "https://www.openstreetmap.org/export/embed.html?bbox=18.3464%2C43.8376%2C18.3664%2C43.8576&layer=mapnik&marker=43.8476%2C18.3564",
  },
];

export default function MapaProdavnica({ categories }) {
  const { translate } = useLanguage();
  const [activeStore, setActiveStore] = useState(stores[0]);
  const [showModal, setShowModal] = useState(false);
  const [modalText, setModalText] = useState({ title: "", description: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    storeName: "",
    city: "",
    email: "",
    pitch: "",
  });
  const [formErrors, setFormErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const errors = {};
    if (!formData.storeName.trim()) errors.storeName = translate("Field is required");
    if (!formData.city.trim()) errors.city = translate("Field is required");
    if (!formData.email.trim()) {
      errors.email = translate("Field is required");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = translate("Invalid email format");
    }
    if (!formData.pitch.trim()) errors.pitch = translate("Field is required");
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const response = await fetch(`${apiUrl}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: formData.storeName,
          email: formData.email,
          subject: "Partnerski prijedlog — nova prodavnica",
          message: `Drzava / Grad / : ${formData.city}\n\nZašto smo dobro uklapanje:\n${formData.pitch}`,
        }),
      });

      const data = await response.json();

      if (data.message === "201") {
        setModalText({
          title: translate("Proposal received"),
          description:
            translate("Thank you for your interest. We carefully consider every proposal and will get back to you if we believe there's a good fit."),
        });
        setFormData({ storeName: "", city: "", email: "", pitch: "" });
      } else {
        setModalText({
          title: translate("Error"),
          description:
            translate("We couldn't receive your proposal. Please contact us directly at contact@bulkaceramics.com"),
        });
      }
    } catch {
      setModalText({
        title: translate("Error"),
        description:
          translate("We couldn't receive your proposal. Please contact us directly at contact@bulkaceramics.com"),
      });
    } finally {
      setIsSubmitting(false);
      setShowModal(true);
    }
  };

  return (
    <div className="bg-white min-h-screen">
      <SEO
        title="Gdje kupiti — Bulka Ceramics"
        description="Pronađite prodavnice koje nose Bulka Ceramics proizvode. Katkus Shop, Sarajevo i još lokacija uskoro."
        type="website"
        name="Bulka Ceramics"
      />

      {showModal && (
        <ThankYou onClose={() => setShowModal(false)} modalText={modalText} />
      )}

      {/* ── PAGE HEADER ─────────────────────────────────────────── */}
      <div className="h-[140px] w-full 768:h-[90px]" />

      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 pb-24">
        {/* Breadcrumb / label */}
        <div className="mb-6">
          <span className="inline-block text-xs font-medium tracking-widest text-brownSkin/50 uppercase border border-brownSkin/20 px-3 py-1 rounded-full">
            {translate("Find us")}
          </span>
        </div>

        <h1 className="text-4xl md:text-5xl font-bold text-brownSkin mb-3 leading-tight">
          {translate("Store map")}
        </h1>
        <p className="text-brownSkin/60 text-base mb-12 max-w-xl">
          {translate("Our products are available at selected locations.")}
        </p>

        {/* ── SPLIT LAYOUT ────────────────────────────────────────── */}
        <div className="flex flex-col lg:flex-row gap-6 min-h-[560px]">

          {/* LEFT — store list */}
          <div className="lg:w-[380px] flex-shrink-0 flex flex-col gap-3">
            {stores.map((store) => (
              <button
                key={store.id}
                onClick={() => setActiveStore(store)}
                className={`text-left w-full rounded-2xl border-2 p-5 transition-all duration-200 ${
                  activeStore.id === store.id
                    ? "border-brownSkin bg-bgSkin shadow-md"
                    : "border-lightGray bg-white hover:border-brownSkin/40 hover:bg-bgSkin/40"
                }`}
              >
                <h3 className="text-lg font-bold text-brownSkin">{store.name}</h3>

                <div className="mt-2 flex items-start gap-2 text-brownSkin/60 text-sm">
                  <svg
                    className="w-4 h-4 flex-shrink-0 mt-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.8"
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.8"
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  <span>
                    {store.address}, {store.city} {store.zip}
                    <br />
                    {store.country}
                  </span>
                </div>

                {/* Active indicator */}
                {activeStore.id === store.id && (
                  <div className="mt-3 flex items-center gap-1.5 text-xs text-brownSkin font-medium">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brownSkin opacity-60" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-brownSkin" />
                    </span>
                    {translate("Shown on the map")}
                  </div>
                )}
              </button>
            ))}

            {/* "More coming soon" placeholder */}
            <div className="rounded-2xl border-2 border-dashed border-lightGray p-5 flex flex-col items-center justify-center text-center min-h-[100px]">
              <svg
                className="w-6 h-6 text-brownSkin/30 mb-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.5"
                  d="M12 4v16m8-8H4"
                />
              </svg>
              <p className="text-xs text-brownSkin/40 font-medium tracking-wide">
                {translate("More locations coming soon")}
              </p>
            </div>
          </div>

          {/* RIGHT — map */}
          <div className="flex-1 rounded-2xl overflow-hidden border-2 border-lightGray min-h-[400px] lg:min-h-0 relative">
            <iframe
              key={activeStore.id}
              title={`Mapa — ${activeStore.name}`}
              src={activeStore.mapUrl}
              className="w-full h-full min-h-[400px]"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />

            {/* Store info overlay */}
            <div className="absolute bottom-4 left-4 right-4 sm:right-auto sm:max-w-xs bg-white/95 backdrop-blur-sm rounded-xl border border-lightGray shadow-lg p-4">
              <p className="font-bold text-brownSkin text-base">{activeStore.name}</p>
              <p className="text-brownSkin/60 text-sm mt-0.5">
                {activeStore.address}, {activeStore.city} {activeStore.zip}
              </p>
            </div>
          </div>
        </div>

        {/* ── PARTNERSHIP SECTION ─────────────────────────────────── */}
        <div className="mt-24">
          <div className="w-full h-px bg-lightGray mb-20" />

          <div className="flex flex-col lg:flex-row gap-16 lg:gap-24 items-start">

            {/* Left — copy */}
            <div className="lg:w-[420px] flex-shrink-0">
              <span className="inline-block text-xs font-medium tracking-widest text-brownSkin/50 uppercase border border-brownSkin/20 px-3 py-1 rounded-full mb-6">
                {translate("Partnership")}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-brownSkin leading-snug mb-4">
                {translate("Your store")}
                <br />
                {translate("on this map?")}
              </h2>
              <p className="text-brownSkin/65 text-base leading-relaxed">
                {translate("Our products can currently be found at selected locations. If you run a store and think our products would be a good fit — we'd love to hear from you. We choose partners carefully.")}
              </p>

              <div className="mt-10 space-y-6">
                {[
                  {
                    icon: (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.6" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    ),
                    title: translate("Selective approach"),
                    desc: translate("Each partner goes through an individual selection process."),
                  },
                  {
                    icon: (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.6" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                      </svg>
                    ),
                    title: translate("Handmade, high value"),
                    desc: translate("Products that sell themselves."),
                  },
                  {
                    icon: (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.6" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    ),
                    title: translate("Growing brand"),
                    desc: translate("An active online community that directs customers to stores."),
                  },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-bgSkin border border-brownSkin/10 flex items-center justify-center text-brownSkin">
                      {item.icon}
                    </div>
                    <div>
                      <p className="font-semibold text-brownSkin text-sm">{item.title}</p>
                      <p className="text-brownSkin/55 text-sm mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — form */}
            <div className="flex-1 w-full">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Store name */}
                <div>
                  <label className="block text-sm font-medium text-brownSkin mb-2">
                    {translate("Store name")} <span className="text-brownSkin/40">*</span>
                  </label>
                  <input
                    type="text"
                    name="storeName"
                    value={formData.storeName}
                    onChange={handleChange}
                    placeholder={translate("e.g. My Store")}
                    className={`w-full border-2 rounded-xl px-4 py-3 text-brownSkin placeholder-brownSkin/30 bg-white focus:outline-none focus:ring-2 focus:ring-brownSkin/20 transition-colors duration-200 ${
                      formErrors.storeName
                        ? "border-red-300 focus:border-red-400"
                        : "border-lightGray focus:border-brownSkin"
                    }`}
                  />
                  {formErrors.storeName && (
                    <p className="mt-1.5 text-xs text-red-500">{formErrors.storeName}</p>
                  )}
                </div>

                {/* City / Location */}
                <div>
                  <label className="block text-sm font-medium text-brownSkin mb-2">
                    {translate("Drzava / Grad")} <span className="text-brownSkin/40">*</span>
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder={translate("Drazava i grad")}
                    className={`w-full border-2 rounded-xl px-4 py-3 text-brownSkin placeholder-brownSkin/30 bg-white focus:outline-none focus:ring-2 focus:ring-brownSkin/20 transition-colors duration-200 ${
                      formErrors.city
                        ? "border-red-300 focus:border-red-400"
                        : "border-lightGray focus:border-brownSkin"
                    }`}
                  />
                  {formErrors.city && (
                    <p className="mt-1.5 text-xs text-red-500">{formErrors.city}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-brownSkin mb-2">
                    {translate("Contact email")} <span className="text-brownSkin/40">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="vas@email.com"
                    className={`w-full border-2 rounded-xl px-4 py-3 text-brownSkin placeholder-brownSkin/30 bg-white focus:outline-none focus:ring-2 focus:ring-brownSkin/20 transition-colors duration-200 ${
                      formErrors.email
                        ? "border-red-300 focus:border-red-400"
                        : "border-lightGray focus:border-brownSkin"
                    }`}
                  />
                  {formErrors.email && (
                    <p className="mt-1.5 text-xs text-red-500">{formErrors.email}</p>
                  )}
                </div>

                {/* Pitch */}
                <div>
                  <label className="block text-sm font-medium text-brownSkin mb-2">
                    {translate("Why do you think we'd be a good fit?")}{" "}
                    <span className="text-brownSkin/40">*</span>
                  </label>
                  <textarea
                    name="pitch"
                    value={formData.pitch}
                    onChange={handleChange}
                    rows={5}
                    placeholder={translate("Tell us about your store, customers, and why you think our products would be a good choice...")}
                    className={`w-full border-2 rounded-xl px-4 py-3 text-brownSkin placeholder-brownSkin/30 bg-white focus:outline-none focus:ring-2 focus:ring-brownSkin/20 transition-colors duration-200 resize-none ${
                      formErrors.pitch
                        ? "border-red-300 focus:border-red-400"
                        : "border-lightGray focus:border-brownSkin"
                    }`}
                  />
                  {formErrors.pitch && (
                    <p className="mt-1.5 text-xs text-red-500">{formErrors.pitch}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full sm:w-auto px-10 py-3.5 rounded-full font-semibold text-sm tracking-wide transition-all duration-300 ${
                    isSubmitting
                      ? "bg-lightGray text-brownSkin/40 cursor-not-allowed"
                      : "bg-brownSkin text-white hover:bg-brownSkin/85 hover:shadow-lg"
                  }`}
                >
                  {isSubmitting ? translate("Sending…") : translate("Send us your proposal")}
                </button>

              
              </form>
            </div>
          </div>
        </div>
      </div>

      <Footer categories={categories} />
    </div>
  );
}
